<script setup lang="ts">
/** DDF — Disclosure of Director's Interest (s.156 / s.165) */
import { computed, inject, ref } from 'vue';
import CddVarField from '../incorporation-pre/cdd/CddVarField.vue';
import { DOC_SCOPE } from '../incorporation-pre/cdd/useCddFill';
import SgPostSigners from './SgPostSigners.vue';
import { ddfSignersKey } from './sgPostSignersContext';
import './allotDoc.css';

let s156Seq = 1, ownSeq = 1, famSeq = 1, odSeq = 1, bcSeq = 1;
const s156Rows = ref<number[]>([1]);
const ownRows = ref<number[]>([1]);
const famRows = ref<number[]>([1]);
const odRows = ref<number[]>([1]);
const bcRows = ref<number[]>([1]);

function addS156() { s156Rows.value.push(++s156Seq); }
function delS156(id: number) {
  if (s156Rows.value.length <= 1) return;
  s156Rows.value = s156Rows.value.filter((x) => x !== id);
}
function addOwn() { ownRows.value.push(++ownSeq); }
function delOwn(id: number) {
  if (ownRows.value.length <= 1) return;
  ownRows.value = ownRows.value.filter((x) => x !== id);
}
function addFam() { famRows.value.push(++famSeq); }
function delFam(id: number) {
  if (famRows.value.length <= 1) return;
  famRows.value = famRows.value.filter((x) => x !== id);
}
function addOd() { odRows.value.push(++odSeq); }
function delOd(id: number) {
  if (odRows.value.length <= 1) return;
  odRows.value = odRows.value.filter((x) => x !== id);
}
function addBc() { bcRows.value.push(++bcSeq); }
function delBc(id: number) {
  if (bcRows.value.length <= 1) return;
  bcRows.value = bcRows.value.filter((x) => x !== id);
}

const scope = inject(DOC_SCOPE, '');
const signersKey = computed(() => {
  const m = String(scope || '').match(/^ddf_(\d+)\//);
  return ddfSignersKey(m?.[1] || 1);
});
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head">
      <div class="doc-lbl-title" style="text-align:center; font-size:15px">DISCLOSURE OF DIRECTOR'S INTEREST</div>
    </div>

    <p class="doc-p">
      To:<br />
      The Board of Directors<br />
      <CddVarField field-key="company_name" label="Company Name" /> (the "Company")<br />
      Company Registration No.: <CddVarField field-key="company_registration_no" label="Registration No." />
    </p>

    <p class="doc-lbl-title">Section 156 — General Notice of Disclosure of Interest</p>
    <p class="doc-p">
      In accordance with Section 156 of the Companies Act 1967, I hereby give general notice that I am also a
      Director, Member, Partner, Officer, or Chief Executive Officer of the corporations or firms set out
      below, and I am to be regarded as interested in any transaction/s which may, after the date of this
      notice, be made with such corporations or firms. I will notify the Company of any changes in respect of
      this notification as soon as practicable.
    </p>
    <table class="cdd-tbl">
      <thead>
        <tr><th>Name of Corporation / Firm</th><th>Nature of Interest</th><th style="width:36px" /></tr>
      </thead>
      <tbody>
        <tr v-for="(id, i) in s156Rows" :key="'s156_'+id">
          <td><CddVarField :field-key="`entity_name_${id}`" :label="`Entity ${i + 1} Name`" /></td>
          <td><CddVarField :field-key="`entity_nature_${id}`" :label="`Entity ${i + 1} Nature`" /></td>
          <td><button type="button" class="cdd-rowbtn" :disabled="s156Rows.length <= 1" @click="delS156(id)">×</button></td>
        </tr>
        <tr><td colspan="3"><button type="button" class="cdd-addrow" @click="addS156">+ Add entity</button></td></tr>
      </tbody>
    </table>
    <p class="doc-attn">
      "Corporation" — any body corporate, whether existing in Singapore or outside.
      "Firm" — includes all firms, local and foreign.
      "Nature of Interest" — Director / Member / Partner / Officer / CEO.
      If no interest to disclose, please indicate "NIL".
    </p>

    <p class="doc-lbl-title">Section 165 — Disclosure of Interest in Shares, Debentures &amp; Securities</p>
    <p class="doc-p">
      In accordance with Section 165 of the Companies Act 1967, I hereby notify the Company of my interests
      (and changes thereto) in the shares, debentures, participatory rights, options, and contracts relating
      to shares of the Company and/or its related corporations as at
      <CddVarField field-key="appointment_date" label="Appointment Date" />,
      being the date on which I was appointed as a Director of the Company. In the event of any changes
      hereafter, I shall notify the Company in writing within two (2) days of the occurrence of the event.
    </p>

    <p class="doc-lbl-title">1. Interest Registered in Own Name</p>
    <table class="cdd-tbl">
      <thead>
        <tr>
          <th>Registered Holder</th><th>Description*</th><th>Amount at Appointment / Beginning of FY</th>
          <th>Balance at End of FY</th><th>Date Acquired / Disposed &amp; Price</th><th style="width:36px" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="(id, i) in ownRows" :key="'own_'+id">
          <td><CddVarField :field-key="`own_holder_${id}`" :label="`Own Holder ${i + 1}`" /></td>
          <td><CddVarField :field-key="`own_desc_${id}`" :label="`Own Desc ${i + 1}`" /></td>
          <td><CddVarField :field-key="`own_amount_${id}`" :label="`Own Amount ${i + 1}`" /></td>
          <td><CddVarField :field-key="`own_balance_${id}`" :label="`Own Balance ${i + 1}`" /></td>
          <td><CddVarField :field-key="`own_acquired_${id}`" :label="`Own Acquired ${i + 1}`" /></td>
          <td><button type="button" class="cdd-rowbtn" :disabled="ownRows.length <= 1" @click="delOwn(id)">×</button></td>
        </tr>
        <tr><td colspan="6"><button type="button" class="cdd-addrow" @click="addOwn">+ Add row</button></td></tr>
      </tbody>
    </table>

    <p class="doc-lbl-title">2. Interest of Immediate Family Members (Spouse, Children incl. step &amp; adopted)</p>
    <table class="cdd-tbl">
      <thead>
        <tr>
          <th>Registered Holder</th><th>Description*</th><th>Amount at Appointment / Beginning of FY</th>
          <th>Balance at End of FY</th><th>Date Acquired / Disposed &amp; Price</th><th style="width:36px" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="(id, i) in famRows" :key="'fam_'+id">
          <td><CddVarField :field-key="`fam_holder_${id}`" :label="`Family Holder ${i + 1}`" /></td>
          <td><CddVarField :field-key="`fam_desc_${id}`" :label="`Family Desc ${i + 1}`" /></td>
          <td><CddVarField :field-key="`fam_amount_${id}`" :label="`Family Amount ${i + 1}`" /></td>
          <td><CddVarField :field-key="`fam_balance_${id}`" :label="`Family Balance ${i + 1}`" /></td>
          <td><CddVarField :field-key="`fam_acquired_${id}`" :label="`Family Acquired ${i + 1}`" /></td>
          <td><button type="button" class="cdd-rowbtn" :disabled="famRows.length <= 1" @click="delFam(id)">×</button></td>
        </tr>
        <tr><td colspan="6"><button type="button" class="cdd-addrow" @click="addFam">+ Add row</button></td></tr>
      </tbody>
    </table>

    <p class="doc-lbl-title">3. Other Deemed Interests (Trust, Associated Persons, etc.)</p>
    <table class="cdd-tbl">
      <thead>
        <tr>
          <th>Registered Holder</th><th>Description*</th><th>Amount at Appointment / Beginning of FY</th>
          <th>Balance at End of FY</th><th>Date Acquired / Disposed &amp; Price</th><th style="width:36px" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="(id, i) in odRows" :key="'od_'+id">
          <td><CddVarField :field-key="`od_holder_${id}`" :label="`Other Holder ${i + 1}`" /></td>
          <td><CddVarField :field-key="`od_desc_${id}`" :label="`Other Desc ${i + 1}`" /></td>
          <td><CddVarField :field-key="`od_amount_${id}`" :label="`Other Amount ${i + 1}`" /></td>
          <td><CddVarField :field-key="`od_balance_${id}`" :label="`Other Balance ${i + 1}`" /></td>
          <td><CddVarField :field-key="`od_acquired_${id}`" :label="`Other Acquired ${i + 1}`" /></td>
          <td><button type="button" class="cdd-rowbtn" :disabled="odRows.length <= 1" @click="delOd(id)">×</button></td>
        </tr>
        <tr><td colspan="6"><button type="button" class="cdd-addrow" @click="addOd">+ Add row</button></td></tr>
      </tbody>
    </table>

    <p class="doc-lbl-title">4. In Name of Bodies Corporate (Director controls 20%+ voting rights)</p>
    <table class="cdd-tbl">
      <thead>
        <tr>
          <th>Registered Holder</th><th>Description*</th><th>Amount at Appointment / Beginning of FY</th>
          <th>Balance at End of FY</th><th>Date Acquired / Disposed &amp; Price</th><th style="width:36px" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="(id, i) in bcRows" :key="'bc_'+id">
          <td><CddVarField :field-key="`bc_holder_${id}`" :label="`Body Holder ${i + 1}`" /></td>
          <td><CddVarField :field-key="`bc_desc_${id}`" :label="`Body Desc ${i + 1}`" /></td>
          <td><CddVarField :field-key="`bc_amount_${id}`" :label="`Body Amount ${i + 1}`" /></td>
          <td><CddVarField :field-key="`bc_balance_${id}`" :label="`Body Balance ${i + 1}`" /></td>
          <td><CddVarField :field-key="`bc_acquired_${id}`" :label="`Body Acquired ${i + 1}`" /></td>
          <td><button type="button" class="cdd-rowbtn" :disabled="bcRows.length <= 1" @click="delBc(id)">×</button></td>
        </tr>
        <tr><td colspan="6"><button type="button" class="cdd-addrow" @click="addBc">+ Add row</button></td></tr>
      </tbody>
    </table>
    <p class="doc-attn">
      * Description of Interests: Shares / Debentures / Rights / Options / Warrants / Contracts / Participatory interests.
      Please indicate "NIL" if no interest is to be disclosed in any category.
    </p>

    <p class="doc-p" style="margin-top:14px">
      I hereby declare that the information provided above is true, accurate, and complete as at the date of this notice.
    </p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Name of Director</td><td><CddVarField field-key="director_name" label="Director Name" /></td></tr>
        <tr><td class="cdd-lcell">ID / Passport No.</td><td><CddVarField field-key="director_identification_no" label="Director ID" /></td></tr>
        <tr><td class="cdd-lcell">Date</td><td><CddVarField field-key="declaration_date" label="Declaration Date" /></td></tr>
        <tr><td class="cdd-lcell">Signature</td><td>_____________________________</td></tr>
      </tbody>
    </table>
    <SgPostSigners :var-key="signersKey" board-label="DIRECTOR (DECLARANT)" />
  </div>
</template>
